import React from "react";
import { useTranslation } from "react-i18next";
import { Apple, ArrowRight, Sparkles } from "lucide-react";

/**
 * AppCta — Compact "Explore in the DONiTSH!T App" block for gateway pages
 * ------------------------------------------------------------------------
 * A single reusable component that lives at the bottom of each ecosystem
 * gateway (Wellness / Relationships / Families / Business / Shop). It is
 * intentionally editorial (not an advert): a small card that echoes the
 * card-surface + gold/black palette used across the rest of the site.
 *
 * Per Phase 9 spec, this does NOT deep-link into the mobile app. It shows
 * the App Store + Google Play badges directly on-page so any device can
 * install without needing a working mobile scheme yet. When Universal
 * Links / assetlinks are eventually configured, this component is the
 * single point of upgrade.
 *
 * All copy is i18n-driven, with `variant` selecting which per-gateway
 * eyebrow / title / body to render. Falls back gracefully to the generic
 * `app_cta.default_*` keys if a variant is missing.
 */

const APP_STORE_URL = "https://apps.apple.com/";
const GOOGLE_PLAY_URL = "https://play.google.com/";

const VARIANT_KEY_MAP = {
  wellness: {
    eyebrow: "app_cta.wellness_eyebrow",
    title: "app_cta.wellness_title",
    body: "app_cta.wellness_body",
  },
  relationships: {
    eyebrow: "app_cta.relationships_eyebrow",
    title: "app_cta.relationships_title",
    body: "app_cta.relationships_body",
  },
  families: {
    eyebrow: "app_cta.families_eyebrow",
    title: "app_cta.families_title",
    body: "app_cta.families_body",
  },
  business: {
    eyebrow: "app_cta.business_eyebrow",
    title: "app_cta.business_title",
    body: "app_cta.business_body",
  },
  shop: {
    eyebrow: "app_cta.shop_eyebrow",
    title: "app_cta.shop_title",
    body: "app_cta.shop_body",
  },
  default: {
    eyebrow: "app_cta.default_eyebrow",
    title: "app_cta.default_title",
    body: "app_cta.default_body",
  },
};

export default function AppCta({ variant = "default", testid = "app-cta" }) {
  const { t } = useTranslation();
  const keys = VARIANT_KEY_MAP[variant] || VARIANT_KEY_MAP.default;

  return (
    <section
      className="app-container py-14 md:py-20"
      data-testid={testid}
    >
      <div
        className="relative card-surface overflow-hidden p-6 md:p-10"
        data-testid={`${testid}-card`}
      >
        {/* Soft gold glow to match AppDownloadSection aesthetic */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -top-20 -right-16 w-[320px] h-[320px] rounded-full opacity-40 blur-3xl"
          style={{
            background:
              "radial-gradient(circle at center, rgba(212,175,55,0.32) 0%, rgba(212,175,55,0.0) 65%)",
          }}
        />

        <div className="relative flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
          {/* Copy */}
          <div className="max-w-[52ch]">
            <div className="text-xs uppercase tracking-[0.28em] gold-text mb-2 flex items-center gap-2">
              <Sparkles className="w-3.5 h-3.5" />
              <span>{t(keys.eyebrow, t("app_cta.default_eyebrow", "— In the DONiTSH!T App"))}</span>
            </div>
            <h3 className="font-display text-2xl md:text-3xl leading-snug tracking-tight">
              {t(keys.title, t("app_cta.default_title", "Take it deeper in the app."))}
            </h3>
            <p className="mt-3 text-sm md:text-base text-white/72 leading-relaxed body-copy">
              {t(keys.body, t("app_cta.default_body", "The DONiTSH!T app is the interactive layer of the ecosystem — personalised, private and always with you."))}
            </p>
          </div>

          {/* Store badges — identical treatment to homepage AppDownloadSection */}
          <div className="flex flex-wrap gap-3 shrink-0" data-testid={`${testid}-badges`}>
            <a
              href={APP_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`${t("app.app_store_pre")} ${t("app.app_store")}`}
              data-testid={`${testid}-badge-app-store`}
              className="group inline-flex items-center gap-3 rounded-xl border border-white/15
                         bg-black/85 px-4 py-3 hover:border-[#C8A24A]/50 hover:bg-black transition-all"
            >
              <Apple className="w-6 h-6 text-white" />
              <div className="leading-tight">
                <div className="text-[10px] tracking-[0.12em] text-white/65 uppercase">
                  {t("app.app_store_pre", "Download on the")}
                </div>
                <div className="font-display text-sm">{t("app.app_store", "App Store")}</div>
              </div>
            </a>

            <a
              href={GOOGLE_PLAY_URL}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`${t("app.google_play_pre")} ${t("app.google_play")}`}
              data-testid={`${testid}-badge-google-play`}
              className="group inline-flex items-center gap-3 rounded-xl border border-white/15
                         bg-black/85 px-4 py-3 hover:border-[#C8A24A]/50 hover:bg-black transition-all"
            >
              <GooglePlayIcon />
              <div className="leading-tight">
                <div className="text-[10px] tracking-[0.12em] text-white/65 uppercase">
                  {t("app.google_play_pre", "Get it on")}
                </div>
                <div className="font-display text-sm">{t("app.google_play", "Google Play")}</div>
              </div>
            </a>
          </div>
        </div>

        {/* Secondary anchor — soft link to homepage app section for full details */}
        <div className="mt-6 text-xs text-white/55 flex items-center gap-2">
          <span>
            {t(
              "app_cta.tagline",
              "DONiTSH!T in your pocket — Wellness, Relationships, Family, Business & Shop."
            )}
          </span>
          <a
            href="/#app-download-section"
            className="inline-flex items-center gap-1 gold-text hover:text-white transition-colors"
            data-testid={`${testid}-learn-more`}
          >
            {t("app_cta.learn_more", "Learn more")}
            <ArrowRight className="w-3 h-3" />
          </a>
        </div>
      </div>
    </section>
  );
}

// Google Play SVG — matches the icon used inside AppDownloadSection
function GooglePlayIcon() {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 512 512"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <defs>
        <linearGradient id="gpa1" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#00D2FF" />
          <stop offset="100%" stopColor="#3A7BD5" />
        </linearGradient>
        <linearGradient id="gpa2" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#FFD200" />
          <stop offset="100%" stopColor="#F7971E" />
        </linearGradient>
        <linearGradient id="gpa3" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#FF416C" />
          <stop offset="100%" stopColor="#FF4B2B" />
        </linearGradient>
        <linearGradient id="gpa4" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#43E97B" />
          <stop offset="100%" stopColor="#38F9D7" />
        </linearGradient>
      </defs>
      <path d="M85 32 L300 247 85 462 Z" fill="url(#gpa1)" />
      <path d="M85 32 L300 247 380 167 165 32 Z" fill="url(#gpa4)" />
      <path d="M85 462 L300 247 380 327 165 462 Z" fill="url(#gpa3)" />
      <path d="M380 167 L460 247 380 327 300 247 Z" fill="url(#gpa2)" />
    </svg>
  );
}
